import { Portal } from "@mui/material";
import { useMemo, useState, useCallback } from "react";
import { RightSidebar } from "../../../../job-page-components/right-sidebar.component";
import { useRefContext } from "../../../../../../context/RefContext";
import { useJobStore } from "@/dentlab/src/store/Jobs";
import { useServiceCatalogStore } from "@/dentlab/src/store/ServiceCatalog";
import { useCatalogsStore } from "@/dentlab/src/store/Catalogs";
import { useCentralStore } from "@/dentlab/src/store/Central";
import { useTemplatesStore } from "@/dentlab/src/store/Templates/templates.store";
import {
	ServiceCatalogFolder,
	ServiceCatalogItem,
} from "@/dentlab/src/store/ServiceCatalog/types";
import { getItemDisplayInfo } from "@/dentlab/src/pages/service-catalog/utils";
import {
	ChevronRight,
	ChevronDown,
	Folder,
	FolderOpen,
	Search,
	X,
	ChevronsDownUp,
	ChevronsUpDown,
	Pencil,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/dentlab/src/components/ui/input";
import { Button } from "@/dentlab/src/components/ui/button";

function getAllFolderIds(folders: ServiceCatalogFolder[]): Set<string> {
	const ids = new Set<string>();
	for (const folder of folders) {
		ids.add(folder.id);
		for (const id of getAllFolderIds(folder.folders)) {
			ids.add(id);
		}
	}
	return ids;
}

interface CatalogSelectionProps {
	jobDocumentId: number;
	onAddTariffById: (
		code_e: string,
		id: string,
		options?: { force: boolean }
	) => void;
	onAddArticleById: (
		code_e: string,
		id: number,
		options?: { force: boolean }
	) => void;
	onAddTemplate: (value: string) => void;
}

export const JobItemsServiceCatalogSelection: React.FC<
	CatalogSelectionProps
> = ({ jobDocumentId, onAddTariffById, onAddArticleById, onAddTemplate }) => {
	const { openedSidebar, closeSidebar } = useJobStore((state) => ({
		openedSidebar: state.openedSidebar,
		closeSidebar: state.closeSidebar,
	}));

	const catalog = useServiceCatalogStore((s) => s.catalog);
	const tariffsLookup = useCatalogsStore((s) => s.tariffsLookup);
	const articles = useCentralStore((s) => s.articles);
	const templatesLookup = useTemplatesStore((s) => s.templatesLookup);

	const navigate = useNavigate();

	const [searchTerm, setSearchTerm] = useState("");
	const [expandedFolders, setExpandedFolders] = useState<Set<string>>(
		new Set()
	);

	const { container } = useRefContext();

	const open =
		openedSidebar?.type === "catalog" &&
		openedSidebar?.documentId === jobDocumentId;

	const toggleFolder = useCallback((folderId: string) => {
		setExpandedFolders((prev) => {
			const next = new Set(prev);
			if (next.has(folderId)) next.delete(folderId);
			else next.add(folderId);
			return next;
		});
	}, []);

	const handleExpandAll = useCallback(() => {
		setExpandedFolders(getAllFolderIds(catalog.tree));
	}, [catalog.tree]);

	const handleCollapseAll = useCallback(() => {
		setExpandedFolders(new Set());
	}, []);

	const handleItemClick = useCallback(
		(item: ServiceCatalogItem, event: React.MouseEvent) => {
			const force = event.metaKey || event.ctrlKey;
			const info = getItemDisplayInfo(
				item,
				tariffsLookup,
				articles,
				templatesLookup
			);
			if (!info) return;

			if (info.type === "tariff") {
				onAddTariffById(info.code, String(info.id), { force });
			} else if (info.type === "article") {
				onAddArticleById(info.code, Number(info.id), { force });
			} else if (info.type === "template") {
				onAddTemplate(info.code);
			}
		},
		[
			tariffsLookup,
			articles,
			templatesLookup,
			onAddTariffById,
			onAddArticleById,
			onAddTemplate,
		]
	);

	const searchResults = useMemo(() => {
		if (!searchTerm.trim()) return null;
		const q = searchTerm.toLowerCase();
		const results: {
			item: ServiceCatalogItem;
			folderPath: string;
		}[] = [];

		const traverse = (folders: ServiceCatalogFolder[], path: string[]) => {
			for (const folder of folders) {
				const currentPath = [...path, folder.name];
				for (const item of folder.items) {
					const info = getItemDisplayInfo(
						item,
						tariffsLookup,
						articles,
						templatesLookup
					);
					if (!info) continue;
					if (
						info.code.toLowerCase().includes(q) ||
						info.description.toLowerCase().includes(q)
					) {
						results.push({
							item,
							folderPath: currentPath.join(" > "),
						});
					}
				}
				traverse(folder.folders, currentPath);
			}
		};

		traverse(catalog.tree, []);
		return results;
	}, [searchTerm, catalog.tree, tariffsLookup, articles, templatesLookup]);

	const renderItem = (
		item: ServiceCatalogItem,
		onClick: (e: React.MouseEvent) => void,
		subtitle?: string
	) => {
		const info = getItemDisplayInfo(
			item,
			tariffsLookup,
			articles,
			templatesLookup
		);
		if (!info) return null;

		return (
			<div
				className="flex items-center gap-2 py-1.5 px-3 hover:bg-gray-100 cursor-pointer transition-colors"
				onClick={onClick}
				data-testid={`catalog-sidebar-item-${info.code}`}
			>
				<span
					className={`text-xs rounded px-1.5 py-0.5 font-mono font-semibold shrink-0 ${
						info.type === "tariff"
							? "bg-blue-100 text-blue-700"
							: info.type === "article"
								? "bg-green-100 text-green-700"
								: "bg-purple-100 text-purple-700"
					}`}
				>
					{info.badge}
				</span>
				<span className="text-xs font-mono text-gray-500 shrink-0">
					{info.code}
				</span>
				<div className="flex flex-col flex-1 min-w-0">
					<span className="text-sm truncate" title={info.description}>
						{info.description}
					</span>
					{subtitle && (
						<span className="text-xs text-gray-400 truncate">
							{subtitle}
						</span>
					)}
				</div>
			</div>
		);
	};

	const renderFolder = (folder: ServiceCatalogFolder, depth: number) => {
		const isExpanded = expandedFolders.has(folder.id);
		return (
			<div key={folder.id}>
				<div
					className="flex items-center gap-1 py-1 px-2 hover:bg-gray-50 cursor-pointer"
					style={{ paddingLeft: depth * 16 + 8 }}
					onClick={() => toggleFolder(folder.id)}
				>
					{isExpanded ? (
						<ChevronDown className="h-4 w-4 text-gray-400 shrink-0" />
					) : (
						<ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />
					)}
					{isExpanded ? (
						<FolderOpen className="h-4 w-4 text-amber-600 shrink-0" />
					) : (
						<Folder className="h-4 w-4 text-amber-600 shrink-0" />
					)}
					<span className="text-sm font-medium truncate">
						{folder.name}
					</span>
				</div>
				{isExpanded && (
					<>
						{folder.folders.map((sub) =>
							renderFolder(sub, depth + 1)
						)}
						{folder.items.map((item, i) => (
							<div
								key={`${folder.id}-${i}`}
								style={{ paddingLeft: depth * 16 + 8 }}
							>
								{renderItem(item, (e) =>
									handleItemClick(item, e)
								)}
							</div>
						))}
					</>
				)}
			</div>
		);
	};

	if (!open) return null;

	return (
		<Portal container={container.current}>
			<RightSidebar
				title="Leistungskatalog"
				onClose={closeSidebar}
				tools={
					<Button
						variant="ghost"
						size="icon"
						className="h-8 w-8"
						onClick={() =>
							navigate("/organisation/leistungskatalog")
						}
						aria-label="Leistungskatalog bearbeiten"
						data-testid="catalog-sidebar-edit"
						title="Leistungskatalog bearbeiten"
					>
						<Pencil className="h-4 w-4" />
					</Button>
				}
			>
				<div className="p-2 border-b space-y-2">
					<div className="flex items-center gap-1">
						<div className="relative flex-1">
							<Search className="absolute left-2 top-2 h-4 w-4 text-gray-400" />
							<Input
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								placeholder="Suchen..."
								className="h-8 pl-8 pr-8 text-sm"
								aria-label="Im Katalog suchen"
								data-testid="catalog-sidebar-search"
							/>
							{searchTerm && (
								<button
									onClick={() => setSearchTerm("")}
									className="absolute right-2 top-2"
									aria-label="Suche leeren"
								>
									<X className="h-4 w-4 text-gray-400" />
								</button>
							)}
						</div>
						{!searchTerm && catalog.tree.length > 0 && (
							<>
								<Button
									variant="ghost"
									size="icon"
									className="h-8 w-8 shrink-0"
									onClick={handleExpandAll}
									aria-label="Alle aufklappen"
									data-testid="catalog-sidebar-expand-all"
									title="Alle aufklappen"
								>
									<ChevronsUpDown className="h-4 w-4" />
								</Button>
								<Button
									variant="ghost"
									size="icon"
									className="h-8 w-8 shrink-0"
									onClick={handleCollapseAll}
									aria-label="Alle zuklappen"
									data-testid="catalog-sidebar-collapse-all"
									title="Alle zuklappen"
								>
									<ChevronsDownUp className="h-4 w-4" />
								</Button>
							</>
						)}
					</div>
				</div>

				<div className="overflow-auto flex-1">
					{catalog.tree.length === 0 ? (
						<div className="p-4 text-sm text-gray-400 text-center">
							Kein Leistungskatalog konfiguriert. Erstellen Sie
							einen unter Mein Labor &gt; Leistungskatalog.
						</div>
					) : searchResults ? (
						searchResults.length === 0 ? (
							<div className="p-4 text-sm text-gray-400 text-center">
								Keine Ergebnisse gefunden
							</div>
						) : (
							searchResults.map((result, i) => (
								<div key={i}>
									{renderItem(
										result.item,
										(e) => handleItemClick(result.item, e),
										result.folderPath
									)}
								</div>
							))
						)
					) : (
						catalog.tree.map((folder) => renderFolder(folder, 0))
					)}
				</div>
			</RightSidebar>
		</Portal>
	);
};
